import { useCallback, useEffect, useState } from 'react';
import type { NewsI, PaginationMetaI } from 'types';
import useDebounce from 'Hooks/useDebounce';
import { getNewsAdmin, deleteNews } from 'Api/news-api';

/** Estado derivado de una noticia (contrato MEX-464): no viene del back. */
export type NewsStatus = 'scheduled' | 'published' | 'expired';

/** Noticia del listado admin con su estado derivado ya calculado. */
export type NewsRowI = NewsI & { status: NewsStatus };

/** Resultado de una operación destructiva, para que la UI reaccione. */
export interface NewsActionResult {
  ok: boolean;
  message?: string;
}

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * `scheduled` si aún no llega su fecha de publicación, `expired` si ya pasó su
 * fecha de expiración; todo lo demás está `published`.
 */
export const deriveNewsStatus = (item: Pick<NewsI, 'publishAt' | 'expiresAt'>, now: Date = new Date()): NewsStatus => {
  if (new Date(item.publishAt) > now) return 'scheduled';
  if (item.expiresAt && new Date(item.expiresAt) <= now) return 'expired';
  return 'published';
};

/**
 * Estado del listado admin de noticias contra `/api/news` (patrón
 * `useInstructors`): búsqueda y paginación server-side, búsqueda debounced que
 * regresa a la primera página, y `remove` que refresca tras borrar.
 */
export const useNews = () => {
  const [news, setNews] = useState<NewsRowI[]>([]);
  const [pagination, setPagination] = useState<PaginationMetaI | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearchRaw] = useState('');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const fetchPage = useCallback(async (targetPage: number, query: string) => {
    setLoading(true);
    const res = await getNewsAdmin({
      page: targetPage,
      limit: PAGE_SIZE,
      search: query.trim() || undefined,
    });
    if (res.error !== null || res.data === null) {
      setNews([]);
      setPagination(null);
      setError(res.error ?? 'FETCH_ERROR');
      setLoading(false);
      return;
    }
    const now = new Date();
    setNews(res.data.items.map(item => ({ ...item, status: deriveNewsStatus(item, now) })));
    const pag = res.data.pagination;
    setPagination(pag);
    setError(null);
    setLoading(false);
    // Si la página quedó fuera de rango (p. ej. tras borrar el último renglón de
    // la última página), retrocede a la última página válida.
    if (pag && pag.totalPages > 0 && targetPage > pag.totalPages) {
      setPage(pag.totalPages);
    }
  }, []);

  // Carga al cambiar página o búsqueda (debounced).
  useEffect(() => {
    fetchPage(page, debouncedSearch);
  }, [page, debouncedSearch, fetchPage]);

  // Cambiar búsqueda regresa a la primera página en el mismo evento (evita el
  // doble fetch de resetear la página en un efecto aparte).
  const setSearch = (value: string) => {
    setSearchRaw(value);
    setPage(1);
  };

  const refresh = () => fetchPage(page, debouncedSearch);

  const remove = async (id: string): Promise<NewsActionResult> => {
    const res = await deleteNews(id);
    if (res.error !== null) return { ok: false, message: res.error };
    await refresh();
    return { ok: true };
  };

  return {
    news,
    pagination,
    page,
    goToPage: setPage,
    loading,
    error,
    search,
    setSearch,
    refresh,
    remove,
  };
};

export default useNews;
